'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

import { sendGTMEvent } from '@/fo/api/gtm';
import FilterIcon from '@/fo/icons/base/icons/filter32.svg';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { UiKitDrawerBase } from '@/fo/ui/kit/drawer/base';
import { UiKitLabelBase } from '@/fo/ui/kit/label/base';

import { Filter, Filters, SelectedFilters } from '../../types';
import { CheckboxFilter } from '../FilterOptions/CheckboxFilter/CheckboxFilter';
import { LabelListFilter } from '../FilterOptions/LabelListFilter/LabelListFilter';
import { RangeFilter } from '../FilterOptions/RangeFilter/RangeFilter';
import { ToggleListFilter } from '../FilterOptions/ToggleListFilter/ToggleListFilter';

import { styles } from './FiltersPanel.styles';

type NativeElement = React.HTMLAttributes<HTMLDivElement>;

export interface FiltersPanelProps {
  onClick?: NativeElement['onClick'];
  filters: Filters['mainFilters'];
  selectedFilters: SelectedFilters;
  onFilterChange: (
    slug: string,
    value: string | [number, number],
    checked: boolean
  ) => void;
  onReset: () => void;
  onShowProducts: () => void;
  productsCount: number;
  onOpenChange?: (open: boolean) => void;
  isLoading?: boolean;
  isOpenModal?: boolean;
  gtmEventCategory?: string;
}

export function FiltersPanel({
  filters = [],
  selectedFilters,
  onFilterChange,
  onReset,
  onShowProducts,
  productsCount = 0,
  onOpenChange,
  isLoading = false,
  isOpenModal = false,
  gtmEventCategory
}: FiltersPanelProps) {
  const slots = styles();
  const t = useTranslations();

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpenModal && isOpen) {
      setIsOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenModal]);

  const openDrawer = (open: boolean) => {
    if (open) {
      setIsOpen(true);
      sendGTMEvent({
        event: 'filter_click',
        eventCategory: gtmEventCategory || ''
      } as Parameters<typeof sendGTMEvent>[0]);
    }
    onOpenChange?.(open);
  };

  const selectedFiltersCount = Object.values(selectedFilters)?.length;

  const trigger = (
    <UiKitButtonBase
      className={slots.filterButton()}
      childrenClassName={slots.filterButtonChildren()}
      color="ghost"
    >
      <FilterIcon className={slots.filterIcon()} />
      {t('filters')}
      {selectedFiltersCount > 0 && (
        <UiKitLabelBase color="orange">{selectedFiltersCount}</UiKitLabelBase>
      )}
    </UiKitButtonBase>
  );

  const getFilterOptions = (filter: Filter) => {
    switch (filter.type) {
      case 'checkbox':
        return (
          <CheckboxFilter
            selected={selectedFilters[filter.slug]}
            onChange={(value, checked) =>
              onFilterChange(filter.slug, value, Boolean(checked))
            }
            {...filter}
          />
        );
      case 'toggleList':
        return (
          <ToggleListFilter
            selected={selectedFilters[filter.slug]}
            onChange={(value, checked) =>
              onFilterChange(filter.slug, value, Boolean(checked))
            }
            {...filter}
          />
        );
      case 'labelList':
        return (
          <LabelListFilter
            selected={selectedFilters[filter?.slug] || false}
            onChange={(value, checked) =>
              onFilterChange(filter.slug, value, Boolean(checked))
            }
            {...filter}
          />
        );
      case 'range':
        return (
          <RangeFilter
            selected={selectedFilters[filter.slug]}
            onChange={(value, checked) =>
              onFilterChange(filter.slug, value, Boolean(checked))
            }
            {...filter}
          />
        );
    }
  };

  return (
    <UiKitDrawerBase
      title={t('filters')}
      direction="left"
      trigger={trigger}
      contentClassName={slots.drawer()}
      withoutMobile
      onOpenChange={openDrawer}
      open={isOpen}
    >
      <div className={slots.base()}>
        <div className={slots.titleWrapper()}>
          <h2 className={slots.title()}>{t('filters')}</h2>
          {Object.keys(selectedFilters).length > 0 && (
            <UiKitButtonBase
              color="ghost"
              onClick={onReset}
              className={slots.resetButton()}
            >
              {t('reset')}
            </UiKitButtonBase>
          )}
        </div>
        <div className={slots.filters()}>
          {filters
            ?.filter(
              (filter) => !filter.isCareSelection && !filter.hideInSideFilters
            )
            .map((filter: Filter, index: number) => (
              <div
                key={filter.slug}
                className={slots.filter({
                  isNextCheckbox:
                    filters[index + 1]?.type === 'checkbox' &&
                    filter.type === 'checkbox'
                })}
              >
                {filter?.label && filter?.slug === 'brand' && (
                  <h2
                    className={slots.filterTitle({
                      hide: filter.type === 'checkbox'
                    })}
                  >
                    {filter.label}
                  </h2>
                )}
                {getFilterOptions(filter)}
              </div>
            ))}
        </div>
        <div className={slots.footer()}>
          <UiKitButtonBase
            className={slots.showProductsButton()}
            onClick={() => {
              sendGTMEvent({
                event: 'apply_filter',
                eventAction: 'main_filter',
                eventCategory: gtmEventCategory || ''
              } as Parameters<typeof sendGTMEvent>[0]);
              onShowProducts();
            }}
            disabled={productsCount === 0}
            loading={isLoading}
          >
            {t('productCatalog.showProductByCount', {
              count: productsCount
            })}
          </UiKitButtonBase>
        </div>
      </div>
    </UiKitDrawerBase>
  );
}
